import { css, styled } from 'styled-components';
import type { Appearance } from '../context/types';
import type { CustomTheme } from '../types';
import { hexToRgba } from '../utils';
import { getSystemMode } from '../utils/theme';
import predefinedThemes from './themes';
import dark from './themes/dark';
import light from './themes/light';

export const getWalletOption = (appearance?: Appearance) => {
  let theme = appearance?.mode ?? getSystemMode();
  if (theme === 'auto') {
    theme = getSystemMode();
  }

  let themeConfig: any = theme === 'light' ? light : dark;
  themeConfig = mergeThemes({ ...themeGlobals.default, ...themeConfig }, appearance?.theme || {});

  const walletTheme: any = {};
  // 强调色
  walletTheme.colorAccent = themeConfig['--pcm-accent-color'];

  // 主要背景色
  walletTheme.colorPrimary = themeConfig['--pcm-body-background'];

  // 主要文字色（使用主按钮文字色）
  walletTheme.colorOnPrimary = themeConfig['--pcm-primary-button-color'];

  // 主要按钮背景色（使用普通状态和悬停状态颜色）
  walletTheme.primaryButtonBackgroundColors = [
    themeConfig['--pcm-primary-button-bankground'],
    themeConfig['--pcm-primary-button-hover-background'],
  ];

  // 图标按钮背景色（使用次要按钮的普通状态和悬停状态颜色）
  walletTheme.primaryIconButtonBackgroundColors = [
    themeConfig['--pcm-accent-color'],
    themeConfig['--pcm-accent-color'],
  ];

  // 图标按钮文字色
  walletTheme.primaryIconTextColor = themeConfig['--pcm-secondary-button-color'];

  // 主要按钮文字色
  walletTheme.primaryButtonTextColor = themeConfig['--pcm-primary-button-color'];

  // 取消按钮背景色（使用次要按钮背景色）
  walletTheme.cancelButtonBackgroundColor = themeConfig['--pcm-secondary-button-bankground'];

  // 背景色数组
  walletTheme.backgroundColors = [
    themeConfig['--pcm-body-background-secondary'],
    [
      [themeConfig['--pcm-body-background'], themeConfig['--pcm-body-background']],
      [themeConfig['--pcm-body-background'], themeConfig['--pcm-body-background']],
    ],
  ];

  // 消息颜色（使用成功和错误颜色）
  walletTheme.messageColors = [themeConfig['--pcm-success-color'], themeConfig['--pcm-error-color']];

  // 边框发光颜色
  walletTheme.borderGlowColors = [themeConfig['--pcm-accent-color'], themeConfig['--pcm-body-color']];

  // 模态遮罩背景色
  walletTheme.modalMaskBackgroundColor = themeConfig['--pcm-overlay-background'];

  // 卡片边框圆角（使用中等圆角值并转换为数字）
  walletTheme.cardBorderRadius = parseInt(themeConfig['--pcm-rounded-xl']);

  const options = {
    themeType: theme,
    customStyle: {
      [theme]: walletTheme,
    },
    language: appearance?.language,
  };
  return options;
};

const mergeThemes = (base: any, override: any) => {
  const result = { ...base };
  Object.keys(override).forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(override, key) && override[key] !== '') {
      result[key] = override[key];
    }
  });
  return result;
};

export const getAuthCoreTheme = (appearance?: Appearance) => {
  let theme = appearance?.mode ?? getSystemMode();
  if (theme === 'auto') {
    theme = getSystemMode();
  }

  let themeConfig: any = theme === 'light' ? light : dark;
  themeConfig = mergeThemes({ ...themeGlobals.default, ...themeConfig }, appearance?.theme || {});

  const authTheme = {
    primaryBtnColor: themeConfig['--pcm-primary-button-color'],
    primaryBtnBackgroundColor: themeConfig['--pcm-primary-button-bankground'],
    // secondaryBtnColor: themeConfig['--pcm-secondary-button-color'],
    // secondaryBtnBackgroundColor: themeConfig['--pcm-secondary-button-bankground'],
    textColor: themeConfig['--pcm-body-color'],
    secondaryTextColor: themeConfig['--pcm-body-color-secondary'],
    themeBackgroundColor: themeConfig['--pcm-body-background'],
    iconBorderColor: themeConfig['--pcm-button-border-color'],
    accentColor: themeConfig['--pcm-accent-color'],
    inputBackgroundColor: themeConfig['--pcm-body-background-secondary'],
    inputBorderColor: themeConfig['--pcm-button-border-color'],
    inputPlaceholderColor: themeConfig['--pcm-body-color-tertiary'],
    cardBorderColor: themeConfig['--pcm-button-border-color'],
    cardUnclickableBackgroundColor: themeConfig['--pcm-body-background-tertiary'],
    cardUnclickableBorderColor: themeConfig['--pcm-button-border-color'],
    cardDividerColor: themeConfig['--pcm-button-border-color'],
    tagBackgroundColor: themeConfig['--pcm-body-background-secondary'],
    modalBackgroundColor: themeConfig['--pcm-body-background'],
    tipsBackgroundColor: themeConfig['--pcm-body-background-secondary'],
    primaryBtnBorderRadius: themeConfig['--pcm-rounded-lg'],
    cardBorderRadius: themeConfig['--pcm-rounded-md'],
    modalBorderRadius: themeConfig['--pcm-rounded-lg'],
  };
  return authTheme;
};

/**
 * Theme variables for the modal
 */
export const themeGlobals = {
  default: {
    '--pcm-font-family': `-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica,
    'Apple Color Emoji', Arial, sans-serif, 'Segoe UI Emoji',
    'Segoe UI Symbol'`,
    '--pcm-overlay-backdrop-filter': 'blur(6px)',
    '--pcm-rounded-sm': '4px',
    '--pcm-rounded-md': '8px',
    '--pcm-rounded-lg': '11px',
    '--pcm-rounded-xl': '22px',
    '--pcm-rounded-full': '9999px',
    '--pcm-error-color': '#EA4335',
    '--pcm-error-background': hexToRgba('#EA4335', 0.2),
    '--pcm-success-color': '#58C08F',
    '--pcm-success-background': hexToRgba('#58C08F', 0.2),
    '--pcm-warning-color': '#F59E0A',
    '--pcm-warning-background': hexToRgba('#F59E0A', 0.2),
    '--pcm-wallet-lable-color': '#33C759',
    '--pcm-wallet-lable-background': hexToRgba('#33C759', 0.2),
  },
};

const themeColors = {
  light: predefinedThemes.base.light,
  dark: predefinedThemes.base.dark,
};

const createCssVars = (
  scheme: {
    [key: string]: string;
  },
  override?: boolean
) => {
  const important = override ? ' !important' : '';
  return css`
    ${Object.keys(scheme).map((key) => {
      const value = scheme[key];
      return value && `${key}:${value}${important};`;
    })}
  `;
};

const themes = {
  default: createCssVars(themeGlobals.default),
  light: createCssVars(themeColors.light),
  dark: createCssVars(themeColors.dark),
};

export const ThemeContainer = styled.div<{
  $useMode?: string;
  $useTheme?: CustomTheme;
}>`
  ${themes.default}

  ${(props) => {
    if (props.$useMode === 'light') {
      return themes.light;
    } else if (props.$useMode === 'dark') {
      return themes.dark;
    } else {
      return css`
        @media (prefers-color-scheme: light) {
          ${themes.light}
        }
        @media (prefers-color-scheme: dark) {
          ${themes.dark}
        }
      `;
    }
  }}
  

  ${(props) => {
    if (props.$useTheme) {
      return createCssVars(props.$useTheme, true);
    }
    return undefined;
  }}


  ${(props) => {
    if (props.$useTheme) {
      const merge = {};
      if (props.$useTheme['--pcm-success-color']) {
        merge['--pcm-success-background'] = hexToRgba(props.$useTheme['--pcm-success-color'], 0.2);
      }

      if (props.$useTheme['--pcm-warning-color']) {
        merge['--pcm-warning-background'] = hexToRgba(props.$useTheme['--pcm-warning-color'], 0.2);
      }

      if (props.$useTheme['--pcm-error-color']) {
        merge['--pcm-error-background'] = hexToRgba(props.$useTheme['--pcm-error-color'], 0.2);
      }

      if (props.$useTheme['--pcm-wallet-lable-color']) {
        merge['--pcm-wallet-lable-background'] = hexToRgba(props.$useTheme['--pcm-wallet-lable-color'], 0.2);
      }

      return createCssVars(merge, true);
    }
    return undefined;
  }}


  all: initial;
  text-align: left;
  text-direction: ltr;
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: auto;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  -webkit-text-stroke: 0.001px transparent;
  text-size-adjust: none;
  font-size: 16px;
  color: var(--pcm-body-color);

  &,
  * {
    font-family: var(--pcm-font-family);
    box-sizing: border-box;

    &:after,
    &:before {
      box-sizing: border-box;
      border: 0 solid #e5e7eb;
    }
  }

  img,
  svg {
    max-width: 100%;
    overflow: visible;
  }
  strong {
    font-weight: 600;
  }
  input:focus-visible,
  a:focus-visible,
  button:focus-visible {
    outline: 1px solid var(--pcm-focus-color, var(--pcm-accent-color));
  }

  input {
    caret-color: var(--pcm-focus-color, var(--pcm-accent-color));
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;
